import React from "react";
import _ from "lodash";
import { FormattedNumber, FormattedMessage } from "react-intl";
import { Action } from "typescript-fsa";

import { Album, Artist, Track } from "@music/models";
import { Album as AlbumEl } from "@music/components/Album";
import { Artist as ArtistEl } from "@music/components/Artist";
import { Track as TrackEl } from "@music/components/Track";
import { canExpand, InjectedCanExpandProps } from "@ui/canExpand";
import { DownloadCsvButton } from "@ui/DownloadCsvButton";
import { InfoPopover } from "@app/modules/ui/InfoPopover";
import { Chart as Model, ChartRow as ModelRow } from "../../models";
import { messages } from "../../messages";
import { ExpandButton } from "@app/modules/ui/ExpandButton";

interface ChartListProps extends InjectedCanExpandProps {
    chart: Model;
    downloadAction: () => Action<any>;
}

interface ChartRowProps {
    row: ModelRow;
    stream: boolean;
}

const ChartRow = ({ row, stream }: ChartRowProps) => {
    let content: JSX.Element = null;
    // Build the count element.
    const count = (
        <FormattedMessage
            {...(stream ? messages.numberStreams : messages.numberDownloads)}
            values={{
                number: <FormattedNumber value={row.streams.total}></FormattedNumber>,
                total: row.streams.total,
            }} />
    );

    if (row.artistId != null) {
        const artist: Artist = row.artist == null ? new Artist({ id: row.artistId }) : row.artist as Artist;
        content = <ArtistEl artist={artist} link={true}>{count}</ArtistEl>;
    }
    else {
        // Get the album object
        let album: Album = new Album({
            id: row.albumId,
            upc: row.upc,
        });
        if (row.album != null) {
            album = row.album as Album;
        }
        // Determine what to display based on whether it's a track or album.
        if (row.isrc != null) {
            // Get the track object
            let track: Track = new Track({
                id: row.trackId,
                isrc: row.isrc,
            });
            if (row.track != null) {
                track = row.track as Track;
            }
            content = <TrackEl track={track} album={album}>{count}</TrackEl>;
        }
        else {
            content = <AlbumEl album={album}>{count}</AlbumEl>;
        }
    }
    return (
        <li className="chart-row">
            {content}
        </li>
    );
};

class ChartList extends React.Component<ChartListProps> {
    public render() {
        const { chart, downloadAction, expanded, toggleExpanded } = this.props;
        let rows = _.map(chart.rows, (row, i) => {
            return <ChartRow key={i} row={row} stream={chart.name.indexOf("stream") === 0} />;
        });
        let button: JSX.Element = null;
        if (expanded) {
            button = <ExpandButton expanded={expanded} toggleExpanded={toggleExpanded} />;
        }
        else if (rows.length > 4) {
            button = <ExpandButton expanded={expanded} toggleExpanded={toggleExpanded} />;
            rows = rows.slice(0, 4);
        }

        return (
            <div className="chart-list">
                { rows.length === 0 &&
                    <FormattedMessage {...messages.chartsNoRows} />
                }
                { rows.length > 0 &&
                    <div className="chart-list__chart">
                        {chart.isNewReleasesChart() &&
                            <InfoPopover
                                id="chart-new-releases-popover"
                                bodyMessage={messages.chartNewReleasesDescription}
                            />
                        }
                        <ol>
                            { rows}
                        </ol>
                        {button}
                        {" "}
                        <DownloadCsvButton actionCreator={downloadAction} />
                    </div>
                }
            </div>
        );
    }
}

const ConnectedChartList = canExpand(ChartList);

export {
    ChartRow,
    ChartList as ChartListPresenter,
    ConnectedChartList as ChartList,
};
